/* eslint-disable max-lines-per-function */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import {
  faArrowLeft,
  faCheck,
  faSpinner,
  faX,
} from '@fortawesome/pro-regular-svg-icons';
import { useAntrag } from '@/domain/arbeitszeiten/hooks/useAntrag';
import dayjs from 'dayjs';
import { AntragInfoText } from '@/domain/arbeitszeiten/components/EditSheet/content/Antrag/AntragInfoText';
import { renderAvatar } from '@/domain/arbeitszeiten/components/EditSheet/content/Antrag/renderAvatar';
import { Label } from '@/components/ui/label';
import { TimeInput } from '@/domain/arbeitszeiten/components/TimeInput';
import { Textarea } from '@/components/ui/textarea';
import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { useAntraegeUpdate } from '@/domain/arbeitszeiten/hooks/useAntraegeUpdate';
import { useProfile } from '@/hooks';
import { AntragVerlauf } from '@/domain/arbeitszeiten/components/AntragSheet/AntragVerlauf';
import { useManagerCanEditArbeitszeiten } from '@/domain/arbeitszeiten/hooks/useManagerCanEditArbeitszeit';

export function AntragSheetWrapper() {
  return <AntragSheet />;
}

// eslint-disable-next-line complexity
export function AntragSheet() {
  const { t } = useTranslation();
  const canEditArbeitszeit = useManagerCanEditArbeitszeiten();

  const { year, month, day, antragId } = useParams();
  const bemerkungRef = useRef<HTMLTextAreaElement>(null);
  const { manager } = useProfile();

  const date = new Date(`${year}-${month}-${day}`);
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { ablehnen, genehmigen, loading: isLoading } = useAntraegeUpdate();

  const result = useAntrag({
    antragId: antragId,
    standortId: standortId,
    datum: dayjs(date).format('YYYY-MM-DD'),
  });
  async function handleApprove() {
    await genehmigen({
      idAntrag: result.data.id || '',
      bemerkungVorgesetzter: bemerkungRef.current.value || '',
      idManagerVorgesetzter: manager.id,
    });
  }

  async function handleReject() {
    await ablehnen({
      idAntrag: result.data.id || '',
      bemerkungVorgesetzter: bemerkungRef.current.value || '',
      idManagerVorgesetzter: manager.id,
    });
  }
  if (result.loading) {
    return <div>Loading</div>;
  }

  if (result.error) {
    return <div>Error: {result.error.message}</div>;
  }

  return (
    <>
      <SheetHeader className="bg-neutrals-200 mb-4">
        <SheetTitle className="flex items-center justify-between p-4">
          <SheetClose className="flex items-center gap-2">
            <FontAwesomeIcon
              className="text-primary-blue-500"
              icon={faArrowLeft}
            />
            <span className="text-sm text-neutrals-800">
              {t('domain.arbeitszeiten.antrag.bearbeiten')}
            </span>
          </SheetClose>
        </SheetTitle>
      </SheetHeader>
      <div className="grow flex flex-col justify-start items-center gap-6">
        <div className="w-full px-2 md:w-[95%]">
          <div className="flex flex-col mb-2 rounded-(--xs,6px) shadow-[0px_4px_8px_0px_rgba(0,0,0,0.14),0px_0px_2px_0px_rgba(0,0,0,0.12)] px-3 pt-4 pb-2 m-2">
            <AntragInfoText
              beginn={result.data.beginn}
              ende={result.data.ende}
              aenderungsdatum={result.data.aenderungsdatum}
              offen={result.data.offen}
            />
            {result.data.bemerkungAntragsteller !== '' && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 text-neutrals-700 font-normal text-sm ps-6">
                  {renderAvatar(
                    result.data.mitarbeiter.bildUrl,
                    result.data.mitarbeiter.vorname,
                    result.data.mitarbeiter.nachname,
                  )}
                  <span>{result.data.bemerkungAntragsteller}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="w-full px-4 md:w-100">
          <Label>{t('domain.arbeitszeiten.antrag.datum')}</Label>
          <div className="flex items-start text-sm">
            {dayjs(result.data.datum).format('dd., D. MMMM')}
          </div>
        </div>
        <div className="w-full md:w-100 px-4 flex justify-between items-start gap-6">
          <div>
            <Label>
              {t('domain.arbeitszeiten.sheet.arbeitszeit.beginnLabel')}
            </Label>
            <TimeInput
              className={`font-bold w-28 ${result.data.beginn2.isChangeRequested ? 'bg-primary-blue-100 border border-primary-blue-700 text-primary-blue-700' : ''}`}
              readOnly
              disabled={true}
              placeholder="--:--"
              value={dayjs(result.data.beginn2.time).format('HH:mm')}
            />
          </div>
          <div className="h-px bg-neutrals-N500 w-32 mt-10" />
          <div>
            <Label>
              {t('domain.arbeitszeiten.sheet.arbeitszeit.endeLabel')}
            </Label>
            <TimeInput
              className={`font-bold w-28 ${result.data.ende2.isChangeRequested ? 'bg-primary-blue-100 border border-primary-blue-700 text-primary-blue-700' : ''}`}
              readOnly
              disabled={true}
              placeholder="--:--"
              value={dayjs(result.data.ende2.time).format('HH:mm')}
            />
          </div>
        </div>
        {canEditArbeitszeit && (
          <>
            <div className="w-full px-4 md:w-100">
              <Label>{t('domain.arbeitszeiten.antrag.bemerkungLabel')}</Label>
              <Textarea
                className="border border-neutrals-300 ring-0 ring-offset-0 focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 outline-none focus:outline-none shadow-none"
                maxLength={250}
                ref={bemerkungRef}
              />
            </div>
            <div className="flex gap-8">
              <Button
                onClick={() => handleReject()}
                disabled={isLoading}
                type="button"
                className="flex items-center rounded-lg bg-primary-red-400 hover:bg-primary-red-400 text-neutrals-100 border-transparent hover:font-semibold hover:text-naturals-N900 w-29.5 gap-1">
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faX}
                  spin={isLoading}
                  size={'sm'}
                />

                <span className="px-1">
                  {t('domain.arbeitszeiten.antrag.ablehnen')}
                </span>
              </Button>
              <Button
                onClick={() => handleApprove()}
                type="button"
                disabled={isLoading}
                className="flex items-center rounded-lg bg-primary-blue-600 hover:bg-primary-blue-600 text-neutrals-100 border-transparent hover:font-semibold hover:text-naturals-900 w-32 gap-1">
                <FontAwesomeIcon
                  icon={isLoading ? faSpinner : faCheck}
                  spin={isLoading}
                  size={'lg'}
                />
                <span className="px-1 ">
                  {t('domain.arbeitszeiten.antrag.annehmen')}
                </span>
              </Button>
            </div>
          </>
        )}

        <AntragVerlauf antrag={result.data} />
      </div>
    </>
  );
}
